import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import useQuery from 'hooks/apollo/useQuery';
import { LEASING_INCOME_SCHEMA_PROPERTIES } from 'features/leasingIncomeSchema/queries';
import MaxHeightTable from 'components/common/MaxHeightTable';
import { RoundedHeaderTableRow, ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow } from '@mui/material';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import FlexBox from 'components/common/FlexBox';
import IconButton from 'components/common/buttons/IconButton';
import LeasingIncomeSchemaPropertyComponent from './LeasingIncomeSchemaPropertyComponent';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE } from 'constants/rightsCodes';
import useSubscription from 'hooks/apollo/useSubscription';
import { useMutation } from 'hooks/apollo/useMutation';
import { useSimpleError } from 'hooks';
import { useToast } from 'hooks/toasts/useToast';
import { useReactiveVar } from '@apollo/client';
import { isIncomeSchemaEditableVar } from '../../variables';
import { ServiceContext } from 'providers/ServiceProvider';
import { SAVE_LEASING_INCOME_SCHEMA_PROPERTIES } from 'features/leasingIncomeSchema/mutations';
import { LEASING_INCOME_SCHEMA_PROPERTIES_SAVED } from 'features/leasingIncomeSchema/subscriptions';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import usePropertiesData, { PropertyContext } from 'features/common/components/property-selector/usePropertiesData';

const LeasingIncomeSchemaPropertiesContainer = ({ id }) => {
  const { t } = useTranslation();
  const showError = useSimpleError();
  const addToast = useToast();
  const [activeService] = useContext(ServiceContext);

  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);
  const isEditable = useReactiveVar(isIncomeSchemaEditableVar);
  const [pending, setPending] = useState(false);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);

  // #region Data loading

  const { loading } = useQuery(LEASING_INCOME_SCHEMA_PROPERTIES, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens((data?.leasingIncomeSchemaProperties || []).map(property => ({ ...property, key: crypto.randomUUID() })));
    }
  });

  const { properties, validation, validate } = usePropertiesData(PropertyContext.FinancialProduct, { valueField: 'value' });

  // #endregion

  // #region Save

  const [saveLeasingIncomeSchemaProperties] = useMutation(SAVE_LEASING_INCOME_SCHEMA_PROPERTIES, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const sanitizedData = data.map(property => {
      const sanitizedProperty = { ...property };

      delete sanitizedProperty.key;
      delete sanitizedProperty.propertyName;
      delete sanitizedProperty.propertyTypeCode;

      if (sanitizedProperty.value instanceof Date) sanitizedProperty.value = sanitizedProperty.value.toLocaleDateString();
      else sanitizedProperty.value = sanitizedProperty.value?.toString() || null;

      return sanitizedProperty;
    });

    setPending(true);
    saveLeasingIncomeSchemaProperties({
      variables: { schemaId: id, properties: sanitizedData }
    });
  };

  useSubscription(LEASING_INCOME_SCHEMA_PROPERTIES_SAVED, {
    onError: showError,
    skip: !id,
    variables: { id },
    onData: ({ client, data }) => {
      const newProperties = data?.data?.leasingIncomeSchemaPropertiesSaved?.properties || [];
      resetLens(newProperties);
      client.writeQuery({
        query: LEASING_INCOME_SCHEMA_PROPERTIES,
        context: { serviceName: activeService?.name },
        variables: { id },
        data: { leasingIncomeSchemaProperties: newProperties }
      });
      setPending(false);
      addToast(t('LeasingIncomeSchema.PropertiesSaved'), 'success');
    }
  });

  // #endregion

  const handleAdd = () => {
    const current = lens |> get;
    (lens |> set)([...current, { key: crypto.randomUUID(), schemaId: id }]);
  };

  const handleDelete = key => {
    const current = lens |> get;
    (lens |> set)(current.filter(property => property.key !== key));
  };

  const hasProperties = (lens.length |> get) > 0;

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const disabled = useMemo(() => !canWrite || pending || !isEditable, [canWrite, pending, isEditable]);

  // #region Side effects

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  useGlobalIsDirty(isDirty);

  // #endregion

  return (
    <FlexBox direction='column' gap={1.5} mt={1.5}>
      <FlexBox justifyContent='end' gap={0.5} px={1}>
        <IconButton type='add' onClick={handleAdd} disabled={disabled} />
        <IconButton type='save' disabled={disabled || !isDirty} onClick={handleSave} />
      </FlexBox>
      <MaxHeightTable maxHeight={500}>
        <MaxHeightTable.Head>
          <RoundedHeaderTableRow>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.PropertyName')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.PropertyValue')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell width='100px' />
          </RoundedHeaderTableRow>
        </MaxHeightTable.Head>
        <MaxHeightTable.Body>
          {loading && (
            <TableRow>
              <TableCell colSpan={3}>
                <WrappedFakeText lines={5} />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            hasProperties &&
            (lens |> sequence).map((itemLens, index) => (
              <LeasingIncomeSchemaPropertyComponent
                key={itemLens.key |> get}
                lens={itemLens}
                properties={properties}
                onDelete={handleDelete}
                validation={validation[index]}
                disabled={disabled}
              />
            ))}
          {!loading && !hasProperties && (
            <TableRow>
              <TableCell colSpan={3} align='center'>
                {t('LeasingIncomeSchema.NoProperties')}
              </TableCell>
            </TableRow>
          )}
        </MaxHeightTable.Body>
      </MaxHeightTable>
    </FlexBox>
  );
};

LeasingIncomeSchemaPropertiesContainer.propTypes = {
  id: PropTypes.number
};

export default LeasingIncomeSchemaPropertiesContainer;
